/**
 * Transactions hooks using React Relay
 *
 * Uses the unified Transaction type that merges charges, refunds,
 * and wallet transactions. Note: Transaction does NOT implement Node,
 * so we use fetchQuery for manual pagination.
 *
 * Note: Run `npm run relay` after making changes to GraphQL queries
 * to regenerate the type artifacts.
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { fetchQuery } from 'react-relay';
import graphql from 'babel-plugin-relay/macro';
import { relayEnvironment } from '../../../../relay';
import type {
  useTransactionsRelayQuery as UseTransactionsRelayQuery,
  TransactionFilterInput,
  TransactionSortInput,
} from './__generated__/useTransactionsRelayQuery.graphql';

// ============================================================================
// GraphQL Definitions
// ============================================================================

/**
 * Direct query for transactions list.
 * Transaction does NOT implement Node, so we can't use @refetchable/@connection.
 * Use fetchQuery with manual cursor pagination instead.
 */
const TransactionsListQuery = graphql`
  query useTransactionsRelayQuery(
    $systemId: ID!
    $userOldId: String
    $first: Int
    $after: String
    $filter: TransactionFilterInput
    $sort: [TransactionSortInput!]
  ) {
    listTransactions(
      systemId: $systemId
      userOldId: $userOldId
      first: $first
      after: $after
      filter: $filter
      sort: $sort
    ) {
      count
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      edges {
        cursor
        node {
          id
          amount
          amountRefunded
          chargeId
          chargeTotal
          created
          description
          holdAmount
          paymentIntentId
          refundDescription
          refunded
          refundStatus
          status
          systemId
          taxAmount
          taxRate
          type
          userOldId
          user {
            id
            firstName
            lastName
            email
            phone
          }
        }
      }
    }
  }
`;

// ============================================================================
// Types
// ============================================================================

export type { TransactionFilterInput, TransactionSortInput };

export interface TransactionUserGraphQL {
  id: string;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  phone?: number | null;
}

export interface TransactionGraphQL {
  id: string;
  amount?: number | null;
  amountRefunded?: number | null;
  chargeId?: string | null;
  chargeTotal?: number | null;
  created?: string | null;
  description?: string | null;
  holdAmount?: number | null;
  paymentIntentId?: string | null;
  refundDescription?: string | null;
  refunded?: boolean | null;
  refundStatus?: string | null;
  status?: string | null;
  systemId?: string | null;
  taxAmount?: string | null;
  taxRate?: string | null;
  type?: string | null;
  userOldId?: string | null;
  user?: TransactionUserGraphQL | null;
}

export interface TransactionQueryFilter {
  systemId: string;
  userOldId?: string;
  type?: string;
  status?: string;
  createdAfter?: string;
  createdBefore?: string;
}

// ============================================================================
// Hooks
// ============================================================================

// Page size for the fetch-all tail (see the fetchAll option below).
const FETCH_ALL_PAGE_SIZE = 100;

/**
 * Hook that provides React Query-like interface for transactions data
 *
 * Requires systemId since listTransactions requires it.
 */
export function useTransactionsRelayQuery(
  filter?: TransactionQueryFilter,
  options?: {
    limit?: number;
    sort?: TransactionSortInput[];
    enabled?: boolean;
    /**
     * After the first page, keep fetching until the list is drained. The
     * merged Transaction resolver can't filter on the user relationship
     * server-side, so a text search has to run client-side over the full
     * set; callers enable this only while a search term is active.
     */
    fetchAll?: boolean;
  }
) {
  const [data, setData] = useState<TransactionGraphQL[] | null>(null);
  const [count, setCount] = useState<number | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const [isFetched, setIsFetched] = useState(false);
  const [isRefetching, setIsRefetching] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [fetchCount, setFetchCount] = useState(0);
  const [pageInfo, setPageInfo] = useState<{
    hasNextPage: boolean;
    endCursor: string | null;
  } | null>(null);
  const loadingNextRef = useRef(false);
  // Bumped whenever the main effect refetches; loadNext discards a result
  // that lands after the query it belonged to was replaced.
  const generationRef = useRef(0);

  const limit = options?.limit ?? 100;
  const sort = useMemo(
    () => options?.sort ?? [{ field: 'CREATED' as const, order: 'DESC' as const }],
    [options?.sort]
  );
  const enabled = options?.enabled !== false && Boolean(filter?.systemId);
  const fetchAll = options?.fetchAll === true;

  const filterKey = useMemo(() => JSON.stringify(filter), [filter]);

  useEffect(() => {
    if (!enabled || !filter?.systemId) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    generationRef.current += 1;

    const doFetch = async () => {
      try {
        if (isFetched) {
          setIsRefetching(true);
        } else {
          setIsLoading(true);
        }

        const transactionFilter = buildRelayFilter(filter);

        const result = await fetchQuery<UseTransactionsRelayQuery>(
          relayEnvironment,
          TransactionsListQuery,
          {
            systemId: filter.systemId,
            userOldId: filter.userOldId,
            first: limit,
            filter: transactionFilter,
            sort,
          }
        ).toPromise();

        if (cancelled) return;

        if (result) {
          const nodes =
            result.listTransactions?.edges?.map((edge) => edge?.node).filter(Boolean) ??
            [];
          setData(nodes as TransactionGraphQL[]);
          setCount(result.listTransactions?.count ?? undefined);
          let currentPageInfo = {
            hasNextPage: result.listTransactions?.pageInfo?.hasNextPage ?? false,
            endCursor: result.listTransactions?.pageInfo?.endCursor ?? null,
          };
          setPageInfo(currentPageInfo);
          setError(null);

          // Show the first page immediately; the tail below streams in
          // behind the table's subtle refetch indicator.
          setIsLoading(false);
          setIsFetched(true);

          while (
            fetchAll &&
            !cancelled &&
            currentPageInfo.hasNextPage &&
            currentPageInfo.endCursor
          ) {
            setIsRefetching(true);
            const tail = await fetchQuery<UseTransactionsRelayQuery>(
              relayEnvironment,
              TransactionsListQuery,
              {
                systemId: filter.systemId,
                userOldId: filter.userOldId,
                first: FETCH_ALL_PAGE_SIZE,
                after: currentPageInfo.endCursor,
                filter: transactionFilter,
                sort,
              }
            ).toPromise();
            if (cancelled) break;

            const tailNodes =
              tail?.listTransactions?.edges?.map((edge) => edge?.node).filter(Boolean) ??
              [];
            setData((prev) => [...(prev ?? []), ...(tailNodes as TransactionGraphQL[])]);
            const nextCursor = tail?.listTransactions?.pageInfo?.endCursor ?? null;
            currentPageInfo = {
              // A repeated or missing cursor would loop forever; stop instead.
              hasNextPage:
                (tail?.listTransactions?.pageInfo?.hasNextPage ?? false) &&
                nextCursor !== null &&
                nextCursor !== currentPageInfo.endCursor,
              endCursor: nextCursor,
            };
            setPageInfo(currentPageInfo);
          }
        }
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err : new Error('Failed to fetch transactions'));
      } finally {
        if (!cancelled) {
          setIsLoading(false);
          setIsFetched(true);
          setIsRefetching(false);
        }
      }
    };

    doFetch();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, filterKey, limit, sort, fetchCount, fetchAll]);

  const refetch = useCallback(() => {
    setFetchCount((c) => c + 1);
  }, []);

  /**
   * Load next page and append to existing data
   */
  const loadNext = useCallback(
    async (pageSize?: number) => {
      if (!filter?.systemId || !pageInfo?.hasNextPage || !pageInfo.endCursor) return;
      // A second call before the append lands would reuse the same cursor
      // and duplicate the page.
      if (loadingNextRef.current) return;
      loadingNextRef.current = true;
      const generation = generationRef.current;

      const transactionFilter = buildRelayFilter(filter);

      try {
        const result = await fetchQuery<UseTransactionsRelayQuery>(
          relayEnvironment,
          TransactionsListQuery,
          {
            systemId: filter.systemId,
            userOldId: filter.userOldId,
            first: pageSize ?? limit,
            after: pageInfo.endCursor,
            filter: transactionFilter,
            sort,
          }
        ).toPromise();

        // The query was replaced (sort/filter/limit change) while this page
        // was in flight; appending it would mix datasets.
        if (generationRef.current !== generation) return;

        if (result) {
          const newNodes =
            result.listTransactions?.edges?.map((edge) => edge?.node).filter(Boolean) ??
            [];
          setData((prev) => [...(prev ?? []), ...(newNodes as TransactionGraphQL[])]);
          setPageInfo({
            hasNextPage: result.listTransactions?.pageInfo?.hasNextPage ?? false,
            endCursor: result.listTransactions?.pageInfo?.endCursor ?? null,
          });
        }
      } catch (err) {
        setError(
          err instanceof Error ? err : new Error('Failed to load more transactions')
        );
      } finally {
        loadingNextRef.current = false;
      }
    },
    [filter, pageInfo, limit, sort]
  );

  return {
    data,
    count,
    isLoading,
    isFetched,
    isRefetching,
    error,
    refetch,
    loadNext,
    hasNextPage: pageInfo?.hasNextPage ?? false,
  };
}

// ============================================================================
// Helpers
// ============================================================================

function buildRelayFilter(
  filter?: TransactionQueryFilter
): TransactionFilterInput | undefined {
  if (!filter) return undefined;

  const relayFilter: TransactionFilterInput = {};

  if (filter.type) {
    relayFilter.type = { eq: filter.type };
  }
  if (filter.status) {
    relayFilter.status = { eq: filter.status };
  }
  if (filter.createdAfter || filter.createdBefore) {
    relayFilter.created = {};
    if (filter.createdAfter) {
      relayFilter.created.greaterThanOrEqual = filter.createdAfter;
    }
    if (filter.createdBefore) {
      relayFilter.created.lessThanOrEqual = filter.createdBefore;
    }
  }

  return Object.keys(relayFilter).length > 0 ? relayFilter : undefined;
}
